import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { User, Phone, Mail, MapPin, Calendar, Edit2, Save, X, ClipboardList, LogOut } from "lucide-react";
import { toast } from "sonner";
import { updatePatient } from "@/services/data";

export default function ProfilePage() {
  const { patient, isLoggedIn, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: patient?.name || "",
    phone: patient?.phone || "",
    address: patient?.address || "",
    city: patient?.city || "",
    pincode: patient?.pincode || "",
  });
  const [saving, setSaving] = useState(false);

  if (!isLoggedIn || !patient) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground mb-4">Please login to view your profile</p>
        <Button onClick={() => navigate("/login")}>Login</Button>
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    await updatePatient(patient.id, form);
    updateProfile(form);
    setSaving(false);
    setEditing(false);
    toast.success("Profile updated successfully");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-foreground">My Profile</h1>
        <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }} className="text-destructive hover:text-destructive">
          <LogOut className="h-4 w-4 mr-1" /> Logout
        </Button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-8 flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">{patient.name}</h2>
            <p className="text-muted-foreground text-sm">{patient.email}</p>
            <Badge className="mt-1 text-xs bg-green-100 text-green-700 border-0">Verified Patient</Badge>
          </div>
          <div className="ml-auto">
            {editing ? (
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saving}><Save className="h-3.5 w-3.5 mr-1" />{saving ? "Saving..." : "Save"}</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}><Edit2 className="h-3.5 w-3.5 mr-1" />Edit</Button>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[
            { icon: User, label: "Full Name", key: "name", value: patient.name, editable: true },
            { icon: Mail, label: "Email", key: "email", value: patient.email, editable: false },
            { icon: Phone, label: "Phone", key: "phone", value: patient.phone, editable: true },
            { icon: Calendar, label: "Date of Birth", key: "dob", value: patient.dob, editable: false },
          ].map(field => (
            <div key={field.key} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <field.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-0.5">{field.label}</p>
                {editing && field.editable ? (
                  <Input className="h-8 text-sm" value={form[field.key as keyof typeof form] || field.value} onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))} />
                ) : (
                  <p className="text-sm font-medium text-foreground">{field.value || "—"}</p>
                )}
              </div>
            </div>
          ))}

          <div className="sm:col-span-2 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-0.5">Address</p>
              {editing ? (
                <div className="space-y-2">
                  <Input className="h-8 text-sm" placeholder="Street Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input className="h-8 text-sm" placeholder="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                    <Input className="h-8 text-sm" placeholder="Pincode" value={form.pincode} onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} />
                  </div>
                </div>
              ) : (
                <p className="text-sm font-medium text-foreground">{[patient.address, patient.city, patient.pincode].filter(Boolean).join(", ") || "—"}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-border px-6 py-4 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => navigate("/my-bookings")}>
            <ClipboardList className="h-4 w-4 mr-2" /> My Bookings
          </Button>
          <Button className="flex-1" onClick={() => navigate("/tests")}>Book a Test</Button>
        </div>
      </div>
    </div>
  );
}
